import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import VisibilityOffIcon from "../icons/visibilityOffIcon";
import VisibilityIcon from "../icons/visibilityIcon";
import { AmexIcon } from "../icons/cardTypes/amex";
import queryString from "query-string";
import creditCardType from "credit-card-type";
import "./payment.scss";
import moment from "moment";
import FormError from "../formError/formError";
import { MasterCardIcon } from "../icons/cardTypes/mastercard";
import { VisaIcon } from "../icons/cardTypes/visa";
import { DiscoverIcon } from "../icons/cardTypes/discover";
import { JcbIcon } from "../icons/cardTypes/jcb";
import { UnionPayIcon } from "../icons/cardTypes/unionpay";
import { MaestroIcon } from "../icons/cardTypes/maestro";
import { MirIcon } from "../icons/cardTypes/mir";
import { DinersClubIcon } from "../icons/cardTypes/dinersClub";
import { HiperIcon } from "../icons/cardTypes/hiper";
import { HipercardIcon } from "../icons/cardTypes/hipercard";
import { EloIcon } from "../icons/cardTypes/elo";
import StoreDetails from "../storeDetails/storeDetails";
import { useSelector, useDispatch } from "react-redux";
import { AppState } from "../../reducers";
import { CardPaymentInterface, StripePaymentInterface } from "../../interfaces/cardPaymentInterface";
import { makeStorePaymentAction, makeStripePaymentAction } from "../../actions/storeActions";
import { StoreActionTypes } from "../../actionTypes/storeActions";
import { PaymentLoader } from "../paymentLoader/paymentLoader";
import { LastPaymentInterface } from "../../interfaces/storeInterfaces";
import PreviousPayment from "../previousPayment/previousPayment";
import apiConfig from "../../config/api";
const Payment = () => {
  const location = useLocation();
  const dispatch = useDispatch();

  const [cardNumber, setCardNumber] = useState<string>("");
  const [cardHolderName, setCardHolderName] = useState<string>("");
  const [cardType, setCardType] = useState<string>("");
  const [orderRef, setOrderRef] = useState<string>("OSB Balance Clearance via PBL")
  const [cardExpiry, setCardExpiry] = useState<string>("");
  const [cardCvv, setCardCvv] = useState<string>("");
  const [isShowCvv, setIsShowCvv] = useState<boolean>(false);
  const [cardExpiryError, setCardExpiryError] = useState<string>("");
  const [retryCard, setRetryCard] = useState<boolean>(false);
  const [paymentProvider, setPaymentProvider] = useState<string>('MYPAY'); // Default provider, dynamically set based on country_id

  const [authKey, setAuthKey] = useState<string>("");
  const [token, setToken] = useState<string>("");
  const storeDetails = useSelector((state: AppState) => state.storeDetails);
  const paymentDetails = useSelector((state: AppState) => state.paymentDetails);

  console.log(">>>>>>>>>", apiConfig.STRIPE_PUBLISHABLE_KEY);
  
  useEffect(() => {
  const { authKey: auth, token: tok, action, last4, amount, txId, cardIssuer } = queryString.parse(location.search);
    if (auth && tok) {
      setAuthKey(auth as string);
      setToken(tok as string);
    }
    
    // Check if payment was successful (after redirect/reload)
    if (action === 'success' && storeDetails.success) {
      // Use real data from URL parameters and store details
      const paymentAmount = amount ? parseFloat(amount as string) : (storeDetails.success.billingDetails?.totalAmount || 0);
      const transactionId = (txId as string) || storeDetails.success.transactionId?.toString() || '';
      const cardLast4 = (last4 as string) || '****';
      const issuer = (cardIssuer as string) || cardLast4;

      dispatch({
        type: StoreActionTypes.MAKE_PAYMENT,
        payload: {
          isLoading: false,
          success: {
            paymentStatus: 1,
            message: "Payment completed successfully",
            transactionLog: {
              id: parseInt(transactionId) || Date.now(),
              createdAt: (storeDetails.success.lastTransactionDetails as any)?.createdAt || new Date().toISOString(),
              topupAmount: paymentAmount.toString(),
              cardIssuer: issuer,
              last4Digits: cardLast4,
              responseMessage: "Payment completed successfully"
            }
          },
          error: null,
        },
      });
    }
  }, [location.search, dispatch, storeDetails.success]);

  useEffect(() => {
    const card = cardNumber.split(" ").join("");
    if (card) {
      if (creditCardType(card).length) {
        setCardType(creditCardType(card)[0].type);
      } else setCardType("unknown");
    } else {
      setCardType("");
    }
  }, [cardNumber]);

  useEffect(() => {
    if (cardExpiry.length > 1) {
      const cardMonth = +cardExpiry.substr(0, 2);
      const cardYear = +cardExpiry.substr(3, 2);
      const currentYear = +moment().format("YY");

      if (cardMonth > 12) {
        setCardExpiryError("Month should be between 01 and 12");
      } else if (cardYear && currentYear && cardYear < currentYear) {
        setCardExpiryError(
          "Year should be equal or greater than the current year"
        );
      } else setCardExpiryError("");
    }

    if (cardExpiry === "") {
      setCardExpiryError("");
    }
  }, [cardExpiry]);

  // Set payment provider based on country_id
  useEffect(() => {
    if (storeDetails.success?.storeDetails?.countryId) {
      const countryId = storeDetails.success.storeDetails.countryId;
      if (countryId === 3) {
        setPaymentProvider('STRIPE');
      } else {
        setPaymentProvider('MYPAY');
      }
    }
  }, [storeDetails.success]);

  const cardNumberValidation = (key: string) => {
    const card = cardNumber.split(" ").join("");
    if (/^[0-9]$/.test(key)) {
      if (card.length % 4 === 0 && cardNumber.length) {
        setCardNumber(cardNumber + " " + key);
      } else {
        setCardNumber(cardNumber + key);
      }
    } else if (key === "Backspace" || key === "Delete") {
      const lastCharIndex = cardNumber.length - 1;
      if (cardNumber[lastCharIndex - 1] === " ") {
        setCardNumber(cardNumber.substr(0, lastCharIndex - 1));
      } else {
        setCardNumber(cardNumber.substr(0, lastCharIndex));
      }
    }
  };

  const cardCvvValidation = (key: string) => {
    if (/^[0-9]$/.test(key) && cardCvv.length < 4) {
      setCardCvv(cardCvv + key);
    }
    if (key === "Backspace" || key === "Delete") {
      const lastCharIndex = cardCvv.length - 1;
      setCardCvv(cardCvv.substr(0, lastCharIndex));
    }
  };

  const cardExpiryValidation = (key: string) => {
    const cardDeadline = cardExpiry.split("/").join();
    if (cardDeadline.length < 5) {
      if (/^[0-9]$/.test(key)) {
        if (cardDeadline.length === 2) {
          setCardExpiry(cardExpiry + "/" + key);
        } else {
          setCardExpiry(cardExpiry + key);
        }
      }
    }
    if (key === "Backspace" || key === "Delete") {
      const lastCharIndex = cardExpiry.length - 1;
      if (cardExpiry[lastCharIndex - 1] === "/") {
        setCardExpiry(cardExpiry.substr(0, lastCharIndex - 1));
      } else {
        setCardExpiry(cardExpiry.substr(0, lastCharIndex));
      }
    }
  };

  const resetPaymentForm = () => {
    setCardCvv("");
    setCardExpiry("");
    setCardNumber("");
    setIsShowCvv(false);
    setCardHolderName("");
    setCardExpiryError("");
  };

  const handleRetryPayment = () => {
    // Clear payment error state immediately to prevent UI glitch
    dispatch({
      type: StoreActionTypes.MAKE_PAYMENT,
      payload: {
        isLoading: false,
        success: null,
        error: null
      }
    });
    resetPaymentForm();
    setTimeout(() => setRetryCard(false), 0);
    setRetryCard(true);
  };

  const makePayment = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    // Clear any previous error state and retry state immediately
    setRetryCard(false);
    dispatch({
      type: StoreActionTypes.MAKE_PAYMENT,
      payload: {
        isLoading: true,
        success: null,
        error: null
      }
    });
    
    if (storeDetails.success) {
      const { postcode, id } = storeDetails.success.storeDetails;
      const transactionUnique = storeDetails.success.transactionId.toString();
      const invoicesNew = storeDetails.success.billingDetails.invoiceIds
      const formattedOrderRef = `${orderRef} Towards Invoices ${invoicesNew?.map(id => `#${id}`).join(', ')}`;

      if (paymentProvider === 'STRIPE') {        
        try {
          const formData = new URLSearchParams();
          formData.append('type', 'card');
          formData.append('card[number]', cardNumber.replace(/\s/g, ''));
          formData.append('card[exp_month]', cardExpiry.split("/")[0]);
          formData.append('card[exp_year]', `20${cardExpiry.split("/")[1]}`);
          formData.append('card[cvc]', cardCvv);
          formData.append('billing_details[name]', cardHolderName);
          formData.append('billing_details[address][postal_code]', postcode);
          formData.append('billing_details[address][country]', 'AU');

          const response = await fetch('https://api.stripe.com/v1/payment_methods', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/x-www-form-urlencoded',
              'Authorization': `Bearer ${apiConfig.STRIPE_PUBLISHABLE_KEY}`,
            },
            body: formData.toString(),
          });

          const stripeResult = await response.json();

          if (stripeResult.error) {
            dispatch({
              type: StoreActionTypes.MAKE_PAYMENT,
              payload: {
                isLoading: false,
                success: null,
                error: stripeResult.error.message || 'Card validation failed'
              }
            });
            return;
          }

          if (!stripeResult.id) {
            dispatch({
              type: StoreActionTypes.MAKE_PAYMENT,
              payload: {
                isLoading: false,
                success: null,
                error: 'Failed to create payment method'
              }
            });
            return;
          }

          // Step 2: Call makeStripePaymentAction with payment method ID
          // The action will fetch fresh store details and handle JWT token
          const stripePayload: StripePaymentInterface = {
            actionType: "invoiceNewCardAdd",
            cardCVV: cardCvv,
            cardExpiryMonth: cardExpiry.split("/")[0],
            cardExpiryYear: cardExpiry.split("/")[1],
            cardNumber: cardNumber.replace(/\s/g, ''),
            customerName: cardHolderName,
            customerPostCode: postcode,
            invoices: invoicesNew,
            isBobcrm: true,
            isFrom: "PAY-BY-LINK",
            orderRef: formattedOrderRef,
            paymentMethodId: stripeResult.id || '',
            provider: 'STRIPE',
            storeId: id.toString(),
            type: "OSB",
            transactionUnique: transactionUnique.split(':')[0],
            authKey: authKey,
            token: token
          };

          dispatch(makeStripePaymentAction(stripePayload));
        } catch (error) {
          // Dispatch error to Redux to show failure UI
          dispatch({
            type: StoreActionTypes.MAKE_PAYMENT,
            payload: {
              isLoading: false,
              success: null,
              error: `Payment failed: ${(error as Error).message}`
            }
          });
        }
      } else {
        // Handle MyPay payment (existing logic)
        const payload: CardPaymentInterface = {
        cardNumber: cardNumber.split(" ").join(""),
        cardCVV: cardCvv,
        cardExpiryMonth: cardExpiry.split("/")[0],
        cardExpiryYear: cardExpiry.split("/")[1],
        customerName: cardHolderName,
        customerPostCode: postcode,
        orderRef: formattedOrderRef,
        transactionUnique,
        storeId: id,
        provider: 'MYPAY',
        invoices: storeDetails.success.billingDetails.invoiceIds,
        actionType: "invoiceNewCardAdd",
        type: "OSB",
        isBobcrm: true,
        isFrom: 'PAY-BY-LINK',
        authKey: authKey,
        token: token
      };
      dispatch(makeStorePaymentAction(payload));
      }
    }
  };

  const renderVisibilityIcon = () => {
    if (isShowCvv) {
      return <VisibilityIcon fill={"#7D7D7D"} />;
    }
    return <VisibilityOffIcon fill={"#7D7D7D"} />;
  };

  const renderCardTypeIcon = () => {
    if (cardType.toLowerCase() === "american-express") {
      return <AmexIcon />;
    } else if (cardType.toLowerCase() === "mastercard") {
      return <MasterCardIcon />;
    } else if (cardType.toLowerCase() === "visa") {
      return <VisaIcon />;
    } else if (cardType.toLowerCase() === "discover") {
      return <DiscoverIcon />;
    } else if (cardType.toLowerCase() === "jcb") {
      return <JcbIcon />;
    } else if (cardType.toLowerCase() === "unionpay") {
      return <UnionPayIcon />;
    } else if (cardType.toLowerCase() === "maestro") {
      return <MaestroIcon />;
    } else if (cardType.toLowerCase() === "mir") {
      return <MirIcon />;
    } else if (cardType.toLowerCase() === "diners-club") {
      return <DinersClubIcon />;
    } else if (cardType.toLowerCase() === "hiper") {
      return <HiperIcon />;
    } else if (cardType.toLowerCase() === "hipercard") {
      return <HipercardIcon />;
    } else if (cardType.toLowerCase() === "elo") {
      return <EloIcon />;
    }
  };

  const renderStoreDetails = () => {
    if (authKey && token) {
      return <StoreDetails authKey={authKey} token={token} />;
    }
  };

  const renderPayMessage = () => {
    if (storeDetails.success) {
      const { currency } = storeDetails.success;
      return `Pay  ${currency.sign}${Math.abs(+storeDetails.success.billingDetails.totalAmount).toFixed(2)}`;
    }
    return null;
  };

  const renderPaymentPage = () => {
    const linkCreatedDate = storeDetails.success?.billingDetails?.linkCreatedDate;
    if (linkCreatedDate) {
      const targetDate = new Date(linkCreatedDate); // Your given date
      const currentDate = new Date();

// Calculate the time difference in milliseconds
      const timeDiff = targetDate.getTime() - currentDate.getTime();

// Convert milliseconds to days
      const diffInDays = timeDiff / (1000 * 60 * 60 * 24);

    if (diffInDays < 8) {
        console.log('Current date is less than 8 days before 2025-08-04');
      } else {
        return (
            <div
                style={{
                  color: '#6c757d', // muted gray text
                  textAlign: 'center',
                  marginTop: '20px',
                  fontSize: '16px',
                  fontWeight: 500,
                  fontFamily: 'Arial, sans-serif',
                  backgroundColor: '#f8f9fa',
                  padding: '12px 16px',
                  border: '1px solid #dee2e6',
                  borderRadius: '6px',
                  maxWidth: '400px',
                  margin: '20px auto',
                }}
            >
              The link you're trying to access has expired!
            </div>
        );
      }
    } else {
      // Current date is less than 8 days before 2025-08-04
    }

    if (storeDetails.isLoading) {
      return (
        <div className="loader-wrapper">
          <div className="loader"></div>
        </div>
      );
    } else if (
        storeDetails.success &&
        parseFloat(storeDetails.success?.billingDetails?.availableBalance) <= 0.00 &&
        parseFloat(storeDetails.success?.billingDetails?.totalAmount) > 0.00
    ) {
      return (
        <div className="card-details">
          <form
            className="card-form"
            autoComplete="off"
            onSubmit={(e) => makePayment(e)}
          >
            {/* Common Name Field */}
            <div className="card-name">
              <div className="form-field">
                <input
                  name="name"
                  value={cardHolderName}
                  type="text"
                  onChange={(e) => setCardHolderName(e.target.value)}
                  placeholder="Name"
                  required
                />
                <label htmlFor="name">Name</label>
              </div>
            </div>

            {/* Traditional Card Fields - Used for both MyPay and Stripe */}
            <div className="card-number card-number-wrapper">
              <div className="form-field">
                <input
                  name="card-number"
                  placeholder="Card Number"
                  value={cardNumber}
                  onKeyDown={(e) => cardNumberValidation(e.key)}
                  inputMode="numeric"
                      required
                    />
                    <label htmlFor="card-number">Card Number</label>
                  </div>
                  <div className="card-type">{renderCardTypeIcon()}</div>
                </div>

                <div className="card-info-more">
                  <div className="form-field">
                    <input
                      name="card-expiry"
                      placeholder="MM/YY"
                      value={cardExpiry}
                      onKeyDown={(e) => cardExpiryValidation(e.key)}
                      inputMode="numeric"
                      required
                    />
                    <label htmlFor="card-expiry">Expiry Date</label>
                    {cardExpiryError && (
                      <FormError errorMessage={cardExpiryError} />
                    )}
                  </div>
                  <div className="cvv-wrapper">
                    <div className="form-field">
                      <input
                        name="card-cvv"
                        placeholder="CVV"
                        value={cardCvv}
                        type={isShowCvv ? "text" : "password"}
                        inputMode="numeric"
                        onKeyDown={(e) => cardCvvValidation(e.key)}
                        required
                      />
                      <label htmlFor="card-cvv">CVV</label>
                    </div>
                    <div
                      className="form-field pointer"
                      onClick={() => setIsShowCvv(!isShowCvv)}
                    >
                      {renderVisibilityIcon()}
                    </div>
                  </div>
                </div>

            <div className="btn-group">
              <div className="form-field">
                <button
                  type="button"
                  onClick={resetPaymentForm}
                  className="btn-reset"
                >
                  Reset
                </button>
                <button 
                  type="submit" 
                  className="btn-pay"
                  disabled={!cardNumber || !cardExpiry || !cardCvv || !cardHolderName || !!cardExpiryError}
                >
                  {renderPayMessage()}
                </button>
              </div>
            </div>
          </form>
        </div>
      );
    } else {
      return (
          <div
              style={{
                color: '#6c757d',
                textAlign: 'center',
                marginTop: '20px',
                fontSize: '16px',
                fontWeight: 500,
                fontFamily: 'Arial, sans-serif',
                backgroundColor: '#f8f9fa',
                padding: '12px 16px',
                border: '1px solid #dee2e6',
                borderRadius: '6px',
                maxWidth: '400px',
                margin: '20px auto',
              }}
          >
            There is no outstanding balance for this takeaway
          </div>
      );
    }
  };

  const renderCardDetails = () => {
    const {
      error: paymentError,
      success: paymentSuccess,
      isLoading: paymentLoading,
    } = paymentDetails;

    // Check retryCard first to prevent UI glitch
    if (retryCard) {
      return renderPaymentPage();
    }
    
    if (paymentLoading) {
      return (
          <div className="payment-loader">
            <PaymentLoader/>
          </div>
      );
    } else if (paymentSuccess) {
      const {paymentStatus, transactionLog} = paymentSuccess;

      if (paymentStatus) {
        return (
          <PreviousPayment
            paymentLog={transactionLog}
            isPaymentSuccessful={true}
            paymentClass={"payment-success"}
          />
        );
      }
      return (
        <>
          <PreviousPayment
            paymentLog={transactionLog}
            isPaymentSuccessful={false}
            paymentClass={"payment-failure"}
          />
          {paymentSuccess.retryPayment ? (
            <button className="btn-retry" onClick={handleRetryPayment}>
              Retry
            </button>
          ) : (
            ""
          )}
        </>
      );
    } else if (paymentError) {
      return (
        <>
          <PreviousPayment
            paymentLog={{}}
            isPaymentSuccessful={false}
            paymentClass={"payment-failure"}
          />
          <button className="btn-retry" onClick={() => setRetryCard(true)}>
            Retry
          </button>
        </>
      );
    }

    if (storeDetails.success) {
      if (retryCard) {
        return renderPaymentPage();
      }

      const {
        isPaymentSuccessful,
        lastTransactionDetails,
        retryPayment,
      } = storeDetails.success;

      if (isPaymentSuccessful) {
        if (Object.keys(lastTransactionDetails).length) {
          return (
            <PreviousPayment
              paymentLog={lastTransactionDetails as LastPaymentInterface}
              isPaymentSuccessful={true}
              paymentClass={"payment-success"}
            />
          );
        }
      } else {
        if (Object.keys(lastTransactionDetails).length) {
          return (
            <>
              <PreviousPayment
                paymentLog={lastTransactionDetails as LastPaymentInterface}
                isPaymentSuccessful={false}
                paymentClass={"payment-failure"}
              />
              {retryPayment ? (
                <button
                  className="btn-retry"
                  onClick={() => setRetryCard(true)}
                >
                  Retry
                </button>
              ) : (
                ""
              )}
            </>
          );
        }
      }
    }

    return renderPaymentPage();
  };

  return (
    <>
    <div className="Payment">
      <div className="payment-details">{renderStoreDetails()}</div>
      <div className="payment-card">{renderCardDetails()}</div>

    </div>
    <div className="logo row m-0 footer-logo">
    <a href={"https://foodhub.co.uk/"} target="_blank" rel="noreferrer">
                  <img
                    className="fh-logo img-fluid d-block mx-auto"
                    src={ "https://public.touch2success.com/static/core/img/foodhub_powered_by_logo.svg"}
                    alt={"FOODHUB"}
                    title={"FOODHUB"}
                    height="100%"
                    width="100%"
                   />
                </a>
    </div>
    </>
  );
};

export default Payment;
